import React from "react";

import cx from "classnames";

import { coalesce } from "@kazoohr/helpers";

import { onBrowser } from "../../helpers/onBrowser";
import { Icon, IconDictionary } from "../Icon";

import styles from "./ProfilePic.scss";

/**
 * Profile Picture Component
 *
 * Displays a user's profile picture or initials.
 */
export function ProfilePic({
  className,
  dimensions,
  user,
  size = "large",
  icon,
  grayscale = false,
  activeFace = false,
}: ProfilePicProps): JSX.Element {
  const targetRef = React.useRef<HTMLDivElement>(null);
  const image = icon ? undefined : imageToRender(user);
  const wrapperSize = useDimensions(targetRef, image);
  const profilePicContainerStyle: React.CSSProperties =
    dimensions && size !== "fluid"
      ? { height: dimensions, width: dimensions }
      : {};
  const profilePicStyle: React.CSSProperties = image
    ? { backgroundImage: `url(${image})` }
    : {};
  const initialsStyle: React.CSSProperties = {};

  // istanbul ignore else - requires accurate DOM measurements, which we don't get in unit tests
  if (size !== "fluid" && dimensions) {
    initialsStyle.fontSize = dimensions / 2;
  } else if (size === "fluid" && !image && wrapperSize.width !== 0) {
    // Default value for font-size will be inherited from picture wrapper.
    initialsStyle.fontSize = wrapperSize.width / 2;
  }

  const profilePicClassName = cx(
    styles.wrapper,
    {
      [styles.initials]: !image,
      [wrapperClass(size)]: !dimensions || size === "fluid",
      [styles["wrapper--hover"]]: !!className?.includes("withHover"),
      [styles["active-face"]]: activeFace,
    },
    className
  );

  const picClassName = cx(styles["pic-wrapper"], {
    [styles.pic]: image,
    [styles.initials]: !image,
    [styles.grayscale]: grayscale,
  });

  const textContent = icon ? (
    <Icon icon={icon} color="white" />
  ) : (
    <>{user.initials}</>
  );

  return (
    <div
      data-testid={`profile-pic-${image ? "image" : "default"}`}
      className={profilePicClassName}
      style={profilePicContainerStyle}
      ref={targetRef}
      data-grayscale={grayscale}
    >
      {image ? (
        <div
          className={picClassName}
          style={profilePicStyle}
          data-testid="profile-pic-child"
        />
      ) : (
        <div
          className={picClassName}
          style={initialsStyle}
          data-testid="profile-pic-child-initials"
        >
          {textContent}
        </div>
      )}
    </div>
  );
}

type ProfilePicSizes = "large" | "medium" | "small" | "fluid";

type AvatarContainer = {
  avatarUrl?: string;
  avatar?: string;
  image160?: string | null;
  image70?: string | null;
  image30?: string | null;
  profileImage?: string;
};

export function imageToRender({
  avatarUrl,
  avatar,
  image160,
  image70,
  image30,
  profileImage,
}: AvatarContainer): string | undefined {
  return coalesce(avatarUrl, avatar, image160, image70, image30, profileImage);
}

/**
 * User type definition for ProfilePic component. It's a type because we don't
 * want callers to have to strip props that are not used by ProfilePic.
 */
export type ProfilePicUser = {
  initials?: string;
  avatar?: string;
  avatarUrl?: string;
  id?: string;
  name?: React.ReactNode | React.ReactNode[] | string | Function;
  jobTitle?: string;
  department?: string;
  image160?: string | null;
  image70?: string | null;
  image30?: string | null;
  primaryGroup?: string;
};

/**
 * Props for ProfilePic component
 */
export interface ProfilePicProps {
  /**
   * User object containing profile information
   */
  user: ProfilePicUser;
  /**
   * Absolute size of the profile pic in pixels
   */
  dimensions?: number;
  /**
   * Size of the profile pic. If dimensions are set,
   * they will override this prop, except for FLUID size.
   */
  size?: ProfilePicSizes;
  /**
   * Whether to apply grayscale filter to the image
   */
  grayscale?: boolean;
  /**
   * Custom CSS class name for the component
   */
  className?: string;
  /**
   * Icon to display instead of initials or image
   */
  icon?: IconDictionary;
  /**
   * Whether the profile pic is an active face. If true, it will have a box shadow around it.
   */
  activeFace?: boolean;
}

function wrapperClass(size: ProfilePicSizes): string {
  switch (size) {
    case "small":
      return styles["wrapper--small"];
    case "medium":
      return styles["wrapper--medium"];
    case "fluid":
      return styles["wrapper--fluid"];
    case "large":
    default:
      return styles["wrapper--large"];
  }
}

// istanbul ignore next - requires accurate DOM measurements, which we don't get in unit tests
function useDimensions(
  targetRef: React.RefObject<HTMLDivElement>,
  image?: string
) {
  const calculatedDimensions = {
    width: !image && targetRef.current ? targetRef.current.offsetWidth : 0,
    height: !image && targetRef.current ? targetRef.current.offsetHeight : 0,
  };

  const [dimensions, setDimensions] = React.useState(calculatedDimensions);

  const handleResize = () => {
    setDimensions(calculatedDimensions);
  };

  React.useEffect(() => {
    if (!onBrowser()) {
      return;
    }

    if (!image) {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [image]);

  React.useEffect(() => {
    if (!image) {
      handleResize();
    }
  }, [image]);
  return dimensions;
}
